import { Button, Form, Modal, Row, Select, Spin } from 'antd';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MediaPlanFormProps } from './MediaPlanForm.types';
import { Title } from '@/components';
import { FORM_FIELD } from './MediaPlanForm.constants';
import { useMediaPlanForm } from './hooks/useMediaPlanForm';
import { StyledRangePicker, StyledDatePicker, StyledTitle, StyledInputNumber, TradingDealHint } from './MediaPlanForm.styles';
import { DATE_FORMAT, NAMESPACE } from '@/constants';
import { CURRENCY_OPTIONS, getCurrencySymbol } from '@/constants/currency';
import { useConfirmModal } from '@/hooks/useConfirmModal';

const { Item } = Form;

const MediaPlanForm: FC<Omit<MediaPlanFormProps, 'onCancel' | 'isOpen'>> = ({ mediaPlan, close }) => {
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const {
    initialValues,
    config,
    clientOptions,
    campaignOptions,
    tradingDealPercent,
    form,
    areClientsLoading,
    areCampaignsLoading,
    handleSubmit,
    isLoading
  } = useMediaPlanForm({
    mediaPlan,
    close
  });
  const selectedCurrency = Form.useWatch('currency', form);

  return (
    <Spin spinning={isLoading} size="large">
      <StyledTitle level={5}>{t('addCampaignDetailsHere')}</StyledTitle>
      <Form form={form} initialValues={initialValues} onFinish={handleSubmit} layout="vertical">
        <Item {...config[FORM_FIELD.CLIENT]}>
          <Select
            loading={areClientsLoading}
            size="large"
            showSearch
            optionFilterProp="label"
            placeholder={t('selectClient')}
            options={clientOptions}
          />
        </Item>
        <Item {...config[FORM_FIELD.CAMPAIGN]}>
          <Select
            disabled={!campaignOptions}
            loading={areCampaignsLoading}
            size="large"
            optionFilterProp="label"
            showSearch
            placeholder={t('selectClient')}
            options={campaignOptions}
          />
        </Item>
        <Item {...config[FORM_FIELD.CAMPAIGN_DATES]}>
          <StyledRangePicker size="large" format={DATE_FORMAT} />
        </Item>
        <Item {...config[FORM_FIELD.SCHEDULE_DATE]}>
          <StyledDatePicker size="large" format={DATE_FORMAT} />
        </Item>
        <Item {...config[FORM_FIELD.TOTAL_BUDGET]}>
          <StyledInputNumber
            disabled
            placeholder={t('inputBudget')}
            min={1}
            size="large"
            prefix={getCurrencySymbol(selectedCurrency)}
          />
        </Item>
        <Item {...config[FORM_FIELD.CURRENCY]}>
          <Select
            size="large"
            placeholder={t('currency')}
            options={CURRENCY_OPTIONS}
          />
        </Item>
        <Item style={{ marginBottom: 0 }} {...config[FORM_FIELD.AGENCY_DISCOUNT]}>
          <StyledInputNumber
            placeholder={t('agencyDiscount')}
            min={0}
            max={100}
            size="large"
            suffix="%"
          />
        </Item>
        {tradingDealPercent != null && (
          <TradingDealHint>
            {t('tradingDealLabel')}: {tradingDealPercent}%
          </TradingDealHint>
        )}
        <Row justify="end">
          <Button disabled={isLoading} size="large" htmlType="submit" type="primary">
            {mediaPlan ? t('save', { ns: NAMESPACE.COMMON }) : t('create', { ns: NAMESPACE.COMMON })}
          </Button>
        </Row>
      </Form>
    </Spin>
  );
};

export default ({ close, isOpen, mediaPlan, ...rest }: MediaPlanFormProps) => {
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const modalContent = mediaPlan ? t('cancelMediaPlanEditingProcess') : t('cancelMediaPlanCreationProcess');
  const title = mediaPlan ? t('editMediaPlan') : t('createMediaPlan');

  const { handleConfirm } = useConfirmModal({
    onCancel: close,
    title: t('cancelModalTitle'),
    content: modalContent,
    okText: t('cancel', { ns: NAMESPACE.COMMON }),
    cancelText: t('close', { ns: NAMESPACE.COMMON })
  });

  return (
    <Modal
      footer={false}
      width={720}
      title={<Title level={4}>{title}</Title>}
      open={isOpen}
      onCancel={handleConfirm}
      destroyOnHidden
    >
      <MediaPlanForm mediaPlan={mediaPlan} close={close} {...rest} />
    </Modal>
  );
};
